import type { FundCategoryAverages } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { ValueCompare } from '#/components/ui/ValueCompare';
import { useFundCategoryAverages } from '#/hooks/useFundCategoryAverages';
import { formatPercent } from '#/utils/format';

import { valueClass } from './StatGroup';

interface CategoryComparedReturnProps {
  /** Display label for the fund's own bar, e.g. `VFIAX`. */
  symbol: string;
  /** The fund's own return for this period. */
  value: number;
  /** Which average to compare against. */
  averageKey: keyof FundCategoryAverages;
  categoryCode: string | null | undefined;
  /** The category's display name; falls back to a generic `Category` label. */
  categoryName?: string | null;
}

/**
 * A single return row's value cell, comparing the fund against its category
 * average.
 *
 * This component — not the surrounding section — subscribes to the category
 * averages query, so the re-render when those averages arrive stops here and
 * never reaches the charts above it. The four rows share one query key, so
 * TanStack Query dedupes them into a single request.
 */
export function CategoryComparedReturn({
  symbol,
  value,
  averageKey,
  categoryCode,
  categoryName,
}: CategoryComparedReturnProps) {
  const averages = useFundCategoryAverages(categoryCode);

  return (
    <ValueCompare
      className={valueClass}
      main={{ label: symbol, value }}
      // A fund with a category always reserves the baseline row, so the layout
      // holds still while the averages load.
      compare={
        categoryCode
          ? {
              label: categoryName ?? 'Category',
              value: averages?.[averageKey] ?? null,
            }
          : undefined
      }
      format={formatPercent}
    />
  );
}
